/*
 * Description:
 * ---------------------
 * Live per-interface traffic panel — a real-time line graph of throughput
 * carried by each WAN interface. Built to answer a single operational
 * question at a glance: "when I switch a policy, which interface actually
 * starts carrying the traffic?"
 *
 * Data source is the SAME GET /health response the WAN Health panel already
 * polls — no new endpoints, no extra data-plane cost. Per-WAN throughput is
 * sampled in-kernel (NIC counters) by the edge once per second and exposed as
 * tx_bytes_per_sec / rx_bytes_per_sec. We poll every 2 s, keep a short rolling
 * history in the browser, and render it as a multi-line time series.
 *
 * ! file InterfaceTrafficPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useWanName } from "./WanNames";
import useVisibleInterval from "../hooks/useVisibleInterval";
import useElementWidth from "../hooks/useElementWidth";

// ── Tunables ─────────────────────────────────────────────────────────────────

const POLL_MS = 2000; // /health poll cadence
const HISTORY_CAP = 460; // ~15 min of 2 s frames — bounded browser memory

const WINDOWS = [
  { label: "1m", sec: 60 },
  { label: "3m", sec: 180 },
  { label: "5m", sec: 300 },
  { label: "10m", sec: 600 },
];

const MODES = [
  { key: "total", label: "Total" },
  { key: "rx", label: "Download" },
  { key: "tx", label: "Upload" },
];

// Stable, high-contrast categorical palette assigned per interface.
const PALETTE = [
  "#2563eb", // blue
  "#16a34a", // green
  "#d97706", // amber
  "#9333ea", // violet
  "#dc2626", // red
  "#0891b2", // cyan
  "#db2777", // pink
  "#65a30d", // lime
];

// ── Formatting ───────────────────────────────────────────────────────────────

// value is in megabits per second.
const fmtMbps = (mbps) => {
  if (mbps == null || !isFinite(mbps)) return "—";
  if (mbps <= 0) return "0";
  if (mbps >= 1000) return `${(mbps / 1000).toFixed(2)} Gbps`;
  if (mbps >= 1) return `${mbps.toFixed(mbps >= 100 ? 0 : 1)} Mbps`;
  const kbps = mbps * 1000;
  if (kbps >= 1) return `${kbps.toFixed(0)} Kbps`;
  return `${(kbps * 1000).toFixed(0)} bps`;
};

const fmtClock = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const fmtAgoShort = (ms) => {
  const sec = Math.round(ms / 1000);
  if (sec <= 0) return "now";
  if (sec < 60) return `-${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `-${m}m${s}s` : `-${m}m`;
};

// bytes/sec → Mbps
const toMbps = (bytesPerSec) =>
  typeof bytesPerSec === "number" && isFinite(bytesPerSec) ? (bytesPerSec * 8) / 1e6 : 0;

// "nice" upper bound for a Y axis given a raw max.
const niceCeil = (v) => {
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const f = v / base;
  let nice;
  if (f <= 1) nice = 1;
  else if (f <= 2) nice = 2;
  else if (f <= 2.5) nice = 2.5;
  else if (f <= 5) nice = 5;
  else nice = 10;
  return nice * base;
};

const valueFor = (entry, mode) => {
  if (!entry) return null;
  if (mode === "tx") return entry.tx;
  if (mode === "rx") return entry.rx;
  return entry.tx + entry.rx;
};

// ── Time-series chart ────────────────────────────────────────────────────────

function TrafficChart({ frames, names, colors, hidden, mode, windowSec }) {
  const wanName = useWanName();
  const [wrapRef, width] = useElementWidth(880);
  const [hover, setHover] = useState(null); // { frameIdx, x }
  const height = 340;
  const padL = 58;
  const padR = 18;
  const padT = 16;
  const padB = 30;
  const plotW = Math.max(10, width - padL - padR);
  const plotH = height - padT - padB;

  const visibleNames = names.filter((n) => !hidden.has(n));

  // Time window: right edge = latest sample (or now), left edge = right - window.
  const tNow =
    frames.length > 0 ? frames[frames.length - 1].ts : Date.now();
  const tMax = tNow;
  const tMin = tMax - windowSec * 1000;

  const windowFrames = useMemo(
    () => frames.filter((f) => f.ts >= tMin - 2000),
    [frames, tMin]
  );

  // Y scale across visible interfaces in window.
  const rawMax = useMemo(() => {
    let m = 0;
    for (const f of windowFrames) {
      for (const n of visibleNames) {
        const v = valueFor(f.byWan[n], mode);
        if (v != null && v > m) m = v;
      }
    }
    return m;
  }, [windowFrames, visibleNames, mode]);

  const yMax = niceCeil(rawMax * 1.15 || 1);

  const xOf = (ts) => padL + ((ts - tMin) / (tMax - tMin || 1)) * plotW;
  const yOf = (v) => padT + (1 - Math.max(0, v) / yMax) * plotH;

  // Y gridlines
  const yTicks = useMemo(() => {
    const n = 4;
    return Array.from({ length: n + 1 }, (_, i) => (yMax / n) * i);
  }, [yMax]);

  // X gridlines (one per window/60s segment, sensible count)
  const xTicks = useMemo(() => {
    const steps = windowSec <= 60 ? 6 : windowSec <= 180 ? 6 : 5;
    return Array.from({ length: steps + 1 }, (_, i) => tMin + ((tMax - tMin) / steps) * i);
  }, [tMin, tMax, windowSec]);

  // Build a path per visible interface.
  const series = useMemo(() => {
    return visibleNames.map((name) => {
      const pts = [];
      for (const f of windowFrames) {
        const v = valueFor(f.byWan[name], mode);
        if (v == null) continue;
        pts.push({ ts: f.ts, v });
      }
      let d = "";
      let area = "";
      pts.forEach((p, i) => {
        const x = xOf(p.ts);
        const y = yOf(p.v);
        d += `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)} `;
      });
      if (pts.length > 0) {
        const x0 = xOf(pts[0].ts);
        const xN = xOf(pts[pts.length - 1].ts);
        area = `${d}L${xN.toFixed(1)} ${(padT + plotH).toFixed(1)} L${x0.toFixed(1)} ${(padT + plotH).toFixed(1)} Z`;
      }
      const last = pts.length ? pts[pts.length - 1] : null;
      return { name, color: colors[name], d: d.trim(), area, last };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowFrames, visibleNames, mode, yMax, width, tMin, tMax]);

  // Hover handling — map mouse X to nearest frame.
  const onMove = useCallback(
    (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      if (mx < padL - 4 || mx > width - padR + 4 || windowFrames.length === 0) {
        setHover(null);
        return;
      }
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < windowFrames.length; i++) {
        const d = Math.abs(xOf(windowFrames[i].ts) - mx);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      if (bestIdx >= 0) setHover({ frameIdx: bestIdx, x: xOf(windowFrames[bestIdx].ts) });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowFrames, width, tMin, tMax]
  );

  const hoverFrame = hover ? windowFrames[hover.frameIdx] : null;
  const hoverRows = hoverFrame
    ? visibleNames
        .map((n) => ({ name: n, color: colors[n], v: valueFor(hoverFrame.byWan[n], mode) }))
        .filter((r) => r.v != null)
        .sort((a, b) => b.v - a.v)
    : [];

  const gradId = (n) => `itp-grad-${n.replace(/[^a-zA-Z0-9]/g, "_")}`;

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <svg
        width={width}
        height={height}
        style={{ display: "block", cursor: "crosshair" }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          {visibleNames.map((n) => (
            <linearGradient key={n} id={gradId(n)} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors[n]} stopOpacity="0.18" />
              <stop offset="100%" stopColor={colors[n]} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {/* Plot background */}
        <rect
          x={padL}
          y={padT}
          width={plotW}
          height={plotH}
          fill="#fbfcfe"
          stroke="#eef2f7"
        />

        {/* Y gridlines + labels */}
        {yTicks.map((t, i) => {
          const y = yOf(t);
          return (
            <g key={`y${i}`}>
              <line
                x1={padL}
                y1={y}
                x2={padL + plotW}
                y2={y}
                stroke="#eef2f7"
                strokeWidth={1}
              />
              <text
                x={padL - 8}
                y={y + 3}
                textAnchor="end"
                fontSize="10"
                fontWeight="600"
                fill="#94a3b8"
              >
                {fmtMbps(t)}
              </text>
            </g>
          );
        })}

        {/* X gridlines + labels */}
        {xTicks.map((t, i) => {
          const x = xOf(t);
          return (
            <g key={`x${i}`}>
              <line
                x1={x}
                y1={padT}
                x2={x}
                y2={padT + plotH}
                stroke="#f4f7fb"
                strokeWidth={1}
              />
              <text
                x={x}
                y={padT + plotH + 16}
                textAnchor="middle"
                fontSize="10"
                fontWeight="600"
                fill="#94a3b8"
              >
                {i === xTicks.length - 1 ? "now" : fmtAgoShort(tMax - t)}
              </text>
            </g>
          );
        })}

        {/* Area fills (subtle) */}
        {series.map(
          (s) =>
            s.area && (
              <path key={`a-${s.name}`} d={s.area} fill={`url(#${gradId(s.name)})`} stroke="none" />
            )
        )}

        {/* Lines */}
        {series.map(
          (s) =>
            s.d && (
              <path
                key={`l-${s.name}`}
                d={s.d}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )
        )}

        {/* Latest-value dots */}
        {series.map(
          (s) =>
            s.last && (
              <circle
                key={`d-${s.name}`}
                cx={xOf(s.last.ts)}
                cy={yOf(s.last.v)}
                r={3.5}
                fill="#fff"
                stroke={s.color}
                strokeWidth={2}
              />
            )
        )}

        {/* Hover crosshair */}
        {hover && hoverFrame && (
          <g>
            <line
              x1={hover.x}
              y1={padT}
              x2={hover.x}
              y2={padT + plotH}
              stroke="#cbd5e1"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            {hoverRows.map((r) => (
              <circle
                key={`h-${r.name}`}
                cx={hover.x}
                cy={yOf(r.v)}
                r={3.5}
                fill={r.color}
                stroke="#fff"
                strokeWidth={1.5}
              />
            ))}
          </g>
        )}
      </svg>

      {/* Hover tooltip */}
      {hover && hoverFrame && hoverRows.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: padT + 6,
            left: Math.min(Math.max(hover.x + 12, padL), width - 190),
            width: 174,
            background: "rgba(15,23,42,0.96)",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 10px",
            pointerEvents: "none",
            boxShadow: "0 8px 24px rgba(15,23,42,0.28)",
            zIndex: 5,
          }}
        >
          <div style={{ fontSize: "0.66rem", color: "#94a3b8", fontWeight: 600, marginBottom: 6 }}>
            {fmtClock(hoverFrame.ts)}
          </div>
          {hoverRows.map((r) => (
            <div
              key={r.name}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 3,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: r.color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {wanName(r.name)}
                </span>
              </span>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                {fmtMbps(r.v)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────────

export default function InterfaceTrafficPanel({ nodeApiUrl, selectedNode }) {
  const wanName = useWanName();
  const [frames, setFrames] = useState([]); // [{ ts, byWan: { name: { tx, rx } } }]
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [mode, setMode] = useState("total");
  const [windowSec, setWindowSec] = useState(180);
  const [hidden, setHidden] = useState(() => new Set());

  // Persistent color assignment so an interface keeps its colour even if a
  // sibling drops out of the list.
  const colorRef = useRef({});
  const orderRef = useRef([]);

  const assignColors = useCallback((names) => {
    for (const n of names) {
      if (!(n in colorRef.current)) {
        const idx = orderRef.current.length % PALETTE.length;
        colorRef.current[n] = PALETTE[idx];
        orderRef.current.push(n);
      }
    }
    return colorRef.current;
  }, []);

  const doFetch = useCallback(async () => {
    if (!selectedNode) return;
    const url = nodeApiUrl("/health");
    if (!url) return;
    try {
      const res = await fetchWithTimeout(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.health) ? data.health : [];
      const now = Date.now();
      const byWan = {};
      for (const w of list) {
        if (!w?.name) continue;
        byWan[w.name] = {
          tx: toMbps(w.tx_bytes_per_sec),
          rx: toMbps(w.rx_bytes_per_sec),
        };
      }
      assignColors(Object.keys(byWan));
      setFrames((prev) => {
        const next = prev.slice();
        next.push({ ts: now, byWan });
        if (next.length > HISTORY_CAP) next.splice(0, next.length - HISTORY_CAP);
        return next;
      });
      setError("");
      setLastFetch(new Date());
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message || "Failed to load traffic data");
    } finally {
      setLoading(false);
    }
  }, [selectedNode, nodeApiUrl, assignColors]);

  // Reset history when switching nodes.
  useEffect(() => {
    setFrames([]);
    setLoading(true);
    setError("");
    colorRef.current = {};
    orderRef.current = [];
  }, [selectedNode]);

  useVisibleInterval(doFetch, POLL_MS, !!selectedNode);

  // Names that have appeared in the recent window, ordered by assignment.
  const names = useMemo(() => {
    const seen = new Set();
    for (const f of frames) for (const n of Object.keys(f.byWan)) seen.add(n);
    return orderRef.current.filter((n) => seen.has(n));
  }, [frames]);

  const colors = colorRef.current;

  const latest = frames.length ? frames[frames.length - 1] : null;

  // Per-interface current readings for the cards / legend.
  const current = useMemo(() => {
    return names.map((n) => {
      const e = latest?.byWan[n];
      const tx = e?.tx ?? 0;
      const rx = e?.rx ?? 0;
      return { name: n, tx, rx, total: tx + rx, color: colors[n] };
    });
  }, [names, latest, colors]);

  const aggregate = useMemo(
    () => current.reduce((acc, c) => acc + valueFor(c, mode), 0),
    [current, mode]
  );

  const dominant = useMemo(() => {
    let best = null;
    for (const c of current) {
      const v = valueFor(c, mode);
      if (v > 0.02 && (!best || v > valueFor(best, mode))) best = c;
    }
    return best;
  }, [current, mode]);

  const toggleHidden = (name) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const modeLabel = MODES.find((m) => m.key === mode)?.label ?? "Total";

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading && frames.length === 0) {
    return (
      <div className="itp-empty">
        <div className="itp-spinner" />
        <span>Sampling interface traffic…</span>
        <style>{styles}</style>
      </div>
    );
  }

  if (error && frames.length === 0) {
    return (
      <div className="itp-empty itp-empty-error">
        <span>⚠ {error}</span>
        <button onClick={doFetch} className="itp-retry">
          Retry
        </button>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="itp-root">
      <style>{styles}</style>

      {/* Toolbar */}
      <div className="itp-toolbar">
        <div className="itp-seg">
          {MODES.map((m) => (
            <button
              key={m.key}
              className={`itp-seg-btn ${mode === m.key ? "active" : ""}`}
              onClick={() => setMode(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="itp-seg">
          {WINDOWS.map((w) => (
            <button
              key={w.sec}
              className={`itp-seg-btn ${windowSec === w.sec ? "active" : ""}`}
              onClick={() => setWindowSec(w.sec)}
            >
              {w.label}
            </button>
          ))}
        </div>
        <div className="itp-live">
          <span className={`itp-dot ${error ? "stale" : ""}`} />
          {error ? "reconnecting" : "live"}
          {lastFetch && !error && (
            <span className="itp-live-ts">· {fmtClock(lastFetch.getTime())}</span>
          )}
        </div>
      </div>

      {/* Summary strip */}
      <div className="itp-summary">
        <div className="itp-summary-card">
          <span className="itp-summary-label">Carrying now ({modeLabel})</span>
          {dominant ? (
            <span className="itp-summary-value">
              <span className="itp-swatch" style={{ background: dominant.color }} />
              {wanName(dominant.name)}
              <span className="itp-summary-sub">{fmtMbps(valueFor(dominant, mode))}</span>
            </span>
          ) : (
            <span className="itp-summary-value itp-idle">idle</span>
          )}
        </div>
        <div className="itp-summary-card">
          <span className="itp-summary-label">Aggregate {modeLabel.toLowerCase()}</span>
          <span className="itp-summary-value">{fmtMbps(aggregate)}</span>
        </div>
        <div className="itp-summary-card">
          <span className="itp-summary-label">Active interfaces</span>
          <span className="itp-summary-value">
            {current.filter((c) => c.total > 0.02).length}
            <span className="itp-summary-sub">of {names.length}</span>
          </span>
        </div>
      </div>

      {/* Chart card */}
      <div className="itp-chart-card">
        <TrafficChart
          frames={frames}
          names={names}
          colors={colors}
          hidden={hidden}
          mode={mode}
          windowSec={windowSec}
        />
      </div>

      {/* Legend / per-interface readouts */}
      <div className="itp-legend">
        {current.map((c) => {
          const isHidden = hidden.has(c.name);
          const live = valueFor(c, mode);
          return (
            <button
              key={c.name}
              className={`itp-legend-chip ${isHidden ? "off" : ""}`}
              onClick={() => toggleHidden(c.name)}
              title={isHidden ? "Show on chart" : "Hide from chart"}
            >
              <span className="itp-legend-head">
                <span className="itp-swatch" style={{ background: c.color }} />
                <span className="itp-legend-name">{wanName(c.name)}</span>
                {dominant?.name === c.name && live > 0.02 && (
                  <span className="itp-legend-tag">active path</span>
                )}
              </span>
              <span className="itp-legend-value" style={{ color: isHidden ? "#94a3b8" : c.color }}>
                {fmtMbps(live)}
              </span>
              <span className="itp-legend-split">
                <span>↓ {fmtMbps(c.rx)}</span>
                <span>↑ {fmtMbps(c.tx)}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Scoped styles ────────────────────────────────────────────────────────────

const styles = `
.itp-root { display: flex; flex-direction: column; gap: 16px; }

.itp-toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
}
.itp-seg {
  display: inline-flex; background: #f1f5f9; border-radius: 9px; padding: 3px;
  border: 1px solid #e2e8f0;
}
.itp-seg-btn {
  border: none; background: transparent; cursor: pointer;
  font-size: 0.78rem; font-weight: 600; color: #64748b;
  padding: 5px 12px; border-radius: 7px; transition: all 0.15s ease;
}
.itp-seg-btn:hover { color: #334155; }
.itp-seg-btn.active {
  background: #fff; color: #0f172a;
  box-shadow: 0 1px 3px rgba(15,23,42,0.12);
}
.itp-live {
  margin-left: auto; display: inline-flex; align-items: center; gap: 7px;
  font-size: 0.76rem; font-weight: 700; color: #16a34a;
  text-transform: uppercase; letter-spacing: 0.05em;
}
.itp-live-ts { color: #94a3b8; font-weight: 600; text-transform: none; letter-spacing: 0; }
.itp-dot {
  width: 9px; height: 9px; border-radius: 50%; background: #16a34a;
  box-shadow: 0 0 0 0 rgba(22,163,74,0.5);
  animation: itp-pulse 1.8s infinite;
}
.itp-dot.stale { background: #f59e0b; animation: none; }
@keyframes itp-pulse {
  0% { box-shadow: 0 0 0 0 rgba(22,163,74,0.45); }
  70% { box-shadow: 0 0 0 6px rgba(22,163,74,0); }
  100% { box-shadow: 0 0 0 0 rgba(22,163,74,0); }
}

.itp-summary { display: flex; gap: 12px; flex-wrap: wrap; }
.itp-summary-card {
  flex: 1; min-width: 160px;
  background: #fff; border: 1px solid #e9eef5; border-radius: 12px;
  padding: 12px 16px; display: flex; flex-direction: column; gap: 6px;
}
.itp-summary-label {
  font-size: 0.68rem; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.06em;
}
.itp-summary-value {
  font-size: 1.15rem; font-weight: 800; color: #0f172a;
  display: inline-flex; align-items: center; gap: 8px;
}
.itp-summary-value.itp-idle { color: #cbd5e1; }
.itp-summary-sub { font-size: 0.78rem; font-weight: 600; color: #94a3b8; }

.itp-chart-card {
  background: #fff; border: 1px solid #e9eef5; border-radius: 14px;
  padding: 14px 14px 6px; box-shadow: 0 1px 2px rgba(15,23,42,0.04);
}

.itp-legend {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}
.itp-legend-chip {
  text-align: left; cursor: pointer;
  background: #fff; border: 1px solid #e9eef5; border-radius: 12px;
  padding: 11px 13px; display: flex; flex-direction: column; gap: 7px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}
.itp-legend-chip:hover { border-color: #cbd5e1; box-shadow: 0 2px 8px rgba(15,23,42,0.06); }
.itp-legend-chip.off { opacity: 0.5; background: #f8fafc; }
.itp-legend-head { display: flex; align-items: center; gap: 8px; min-width: 0; }
.itp-legend-name {
  font-size: 0.85rem; font-weight: 700; color: #0f172a;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.itp-legend-tag {
  margin-left: auto; font-size: 0.6rem; font-weight: 800; color: #16a34a;
  background: #dcfce7; border-radius: 5px; padding: 2px 6px;
  text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap;
}
.itp-legend-value { font-size: 1.35rem; font-weight: 800; line-height: 1; }
.itp-legend-split {
  display: flex; gap: 12px; font-size: 0.72rem; font-weight: 600; color: #94a3b8;
}
.itp-swatch { width: 11px; height: 11px; border-radius: 3px; flex-shrink: 0; }

.itp-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; padding: 64px 24px; color: #94a3b8;
  font-size: 0.9rem; font-weight: 600;
}
.itp-empty-error { color: #dc2626; }
.itp-spinner {
  width: 30px; height: 30px; border-radius: 50%;
  border: 3px solid #e2e8f0; border-top-color: #2563eb;
  animation: itp-spin 0.8s linear infinite;
}
@keyframes itp-spin { to { transform: rotate(360deg); } }
.itp-retry {
  border: 1px solid #cbd5e1; background: #fff; color: #334155;
  font-size: 0.8rem; font-weight: 700; padding: 6px 16px; border-radius: 8px;
  cursor: pointer;
}
.itp-retry:hover { background: #f8fafc; }
`;
